import { Injectable, signal } from '@angular/core';
import { UserProfile } from '../models/auth.model';

const ACCESS_TOKEN_KEY = 'pt_access_token';
const REFRESH_TOKEN_KEY = 'pt_refresh_token';
const USER_PROFILE_KEY = 'pt_user_profile';

/**
 * مدیریت ذخیره و بازیابی Access/Refresh Token و پروفایل کاربر.
 * از localStorage استفاده می‌کند (در یک محیط با حساسیت بالاتر می‌توان به
 * httpOnly cookie منتقل کرد، اما برای SPA + API جدا این روش رایج‌ترین است).
 */
@Injectable({ providedIn: 'root' })
export class TokenStorageService {
  /** سیگنال reactive برای وضعیت لاگین - کامپوننت‌ها می‌توانند مستقیماً subscribe کنند */
  readonly currentUser = signal<UserProfile | null>(this.loadUserFromStorage());

  getAccessToken(): string | null {
    return localStorage.getItem(ACCESS_TOKEN_KEY);
  }

  getRefreshToken(): string | null {
    return localStorage.getItem(REFRESH_TOKEN_KEY);
  }

  setTokens(accessToken: string, refreshToken: string): void {
    localStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
    localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
  }

  setUser(user: UserProfile): void {
    localStorage.setItem(USER_PROFILE_KEY, JSON.stringify(user));
    this.currentUser.set(user);
  }

  /**
   * به‌روزرسانی فقط فیلد hasCompletedOnboarding روی کاربر کش‌شده، بدون نیاز
   * به یک فراخوانی جدید به /users/me. این متد بعد از تکمیل یا رد کردن
   * (Skip) موفق مرحله Onboarding باید صدا زده شود؛ در غیر این صورت
   * authGuard همچنان hasCompletedOnboarding=false قدیمی را در localStorage
   * می‌بیند و کاربر را دوباره به /onboarding برمی‌گرداند حتی بعد از
   * تکمیل موفق آن در سرور.
   */
  markOnboardingCompleted(): void {
    const user = this.currentUser();
    if (!user) return;
    this.setUser({ ...user, hasCompletedOnboarding: true });
  }

  clear(): void {
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    localStorage.removeItem(USER_PROFILE_KEY);
    this.currentUser.set(null);
  }

  isLoggedIn(): boolean {
    return !!this.getAccessToken();
  }

  private loadUserFromStorage(): UserProfile | null {
    const raw = localStorage.getItem(USER_PROFILE_KEY);
    if (!raw) return null;
    try {
      return JSON.parse(raw) as UserProfile;
    } catch {
      return null;
    }
  }
}
